import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { filterForGPUInstances } from "components/Environment/Create/EnvironmentCreatePublic";
import { GPUInstanceType } from "types/InstanceTypes";
import { CurrencyDisplay } from "components/OrgPages/types";
import agent from "server";

import { OrgContext } from "./OrgContext";
import { CloudCredContext } from "./CloudCredContext";
import { UserContext } from "./UserContext";

export interface IInstanceTypesContext {
  isInstancesLoading: boolean;
  allInstancesAvailable: GPUInstanceType[];
  GPUInstancesAvailable: GPUInstanceType[];
  CPUInstancesAvailable: GPUInstanceType[];
  updateInstanceTypes: (orgId: string, imageID?: string) => Promise<any>;
  currencyDisplay: CurrencyDisplay;
  leastExpensiveAvailableInstance: GPUInstanceType | null;
  mostExpensiveAvailableInstance: GPUInstanceType | null;
}

export const instanceTypesContextDefaultValues: IInstanceTypesContext = {
  isInstancesLoading: false,
  allInstancesAvailable: [],
  GPUInstancesAvailable: [],
  CPUInstancesAvailable: [],
  updateInstanceTypes: async () => null,
  currencyDisplay: "credits",
  leastExpensiveAvailableInstance: null,
  mostExpensiveAvailableInstance: null,
};

export const InstanceTypesContext = createContext<IInstanceTypesContext>(
  instanceTypesContextDefaultValues,
);

interface Props {
  children: React.ReactNode;
}

const InstanceTypesContextProvider: React.FC<Props> = ({ children }) => {
  const { activeOrg, activeOrgId } = useContext(OrgContext);
  const { cloudCreds } = useContext(CloudCredContext);
  const { isAuthenticated, me } = useContext(UserContext);
  const [isInstancesLoading, setIsInstancesLoading] = useState(false);
  const [allInstancesAvailable, setAllInstancesAvailable] = useState<
    GPUInstanceType[]
  >([]);
  const [GPUInstancesAvailable, setGPUInstancesAvailable] = useState<
    GPUInstanceType[]
  >([]);
  const [CPUInstancesAvailable, setCPUInstancesAvailable] = useState<
    GPUInstanceType[]
  >([]);

  const updateInstanceTypes = useCallback(
    async (orgId: string, imageID?: string): Promise<any> => {
      setIsInstancesLoading(true);
      try {
        const res = await agent.Instances.getAllInstanceTypesAvailable(
          orgId,
          imageID,
        );

        if (
          res.success &&
          res.data &&
          Object.keys(res.data).length !== 0 &&
          res.data.allInstanceTypes?.length > 0
        ) {
          const instanceTypes = res.data.allInstanceTypes as GPUInstanceType[];
          setAllInstancesAvailable(instanceTypes);
          setGPUInstancesAvailable(filterForGPUInstances(instanceTypes));
          setCPUInstancesAvailable(
            instanceTypes.filter(
              (instance) =>
                !instance.supported_gpus ||
                instance.supported_gpus.length === 0,
            ),
          );
          return res.data;
        }
        return null;
      } catch (error) {
        console.error("Failed to load instance types:", error);
        return null;
      } finally {
        setIsInstancesLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (activeOrgId && isAuthenticated && me?.id) {
      void updateInstanceTypes(activeOrgId);
    }
  }, [activeOrgId, cloudCreds, isAuthenticated, me?.id, updateInstanceTypes]);

  const currencyDisplay =
    (activeOrg?.labels?.currencyDisplay as CurrencyDisplay) || "credits";
  const [leastExpensiveAvailableInstance, mostExpensiveAvailableInstance] =
    useMemo(() => {
      const pricedInstanceTypes = allInstancesAvailable
        .filter((instance) => instance.base_price)
        .sort(
          (a, b) => Number(a.base_price!.amount) - Number(b.base_price!.amount),
        );
      return [
        pricedInstanceTypes[0] || null,
        pricedInstanceTypes[pricedInstanceTypes.length - 1] || null,
      ];
    }, [allInstancesAvailable]);

  return (
    <InstanceTypesContext.Provider
      value={{
        isInstancesLoading,
        allInstancesAvailable,
        GPUInstancesAvailable,
        CPUInstancesAvailable,
        updateInstanceTypes,
        currencyDisplay,
        leastExpensiveAvailableInstance,
        mostExpensiveAvailableInstance,
      }}
    >
      {children}
    </InstanceTypesContext.Provider>
  );
};

export default InstanceTypesContextProvider;
